import { defineStore } from '/src/core/store';

import {
	getWelcomeSectionConfiguration,
	getCodeCredentialsConfiguration,
	getFaceIdConfiguration,
	getQrCodeConfiguration,
	getNfcConfiguration,
} from '/src/apis/configurations.js';
import { getDevice } from '/src/apis/device.js';
import { getDeviceTypes } from '/src/apis/device-types.js';
import { useFaceStore } from '/src/store/stores/useFaceStore.js';
import { useNfcStore } from '/src/store/stores/useNfcStore.js';
import { useDark } from '/src/composables/useDark.js';
import { arrayToObject } from '/src/utils/arrays.js';
import { BETA_TESTING_FEATURES } from '/src/config/betaTestingFeatures.js';

const {
	setAudio,
	setThreshold,
	setConfidence,
	setIsLiveMode,
	setFlash,
	setLastUpdate,
	setFaceDataVersion,
} = useFaceStore();

// nfc store
const {
	setContinuous,
} = useNfcStore();

// theme
const isDark = useDark();

export const useDeviceStore = defineStore('device', {
	state: () => ({
		device: null,
		locale: 'es-ES',
		configs: [],
		types: [],
		countdown: 6,
		employeeList: true,
		busy: false,
	}),
	persist: {
		storage: 'native',
		pick: ['device', 'locale', 'configs', 'types', 'countdown', 'employeeList', 'busy'],
		migrate: 'local',
	},
	getters: {
		name () {
			return this.device?.name;
		},
		companies () {
			return this.device?.companies;
		},
		companyIds () {
			return this.companies?.map(company => company.id);
		},
		betaTestingFeatures () {
			return (this.companies ?? [])
				.flatMap(company => company?.betaTestingFeatures?.split(',').map(feature => feature.trim()) ?? []);
		},
		isFacialRecognitionAmazon () {
			return this.betaTestingFeatures.includes(BETA_TESTING_FEATURES.FACIAL_RECOGNITION_AWS);
		},
		office () {
			return this.device?.office;
		},
		officeId () {
			return this.office?.id;
		},
		offices () {
			return [this.device?.office].filter(office => Boolean(office));
		},
		officeIds () {
			return this.offices?.map(office => office.id);
		},
		groupId () {
			return this.device?.entityGroupId;
		},
		departmentId () {
			return this.device?.departmentId;
		},
		status () {
			return this.device?.status;
		},
		allowCheckOut () {
			return this.device?.allowCheckOut;
		},
		allowPause () {
			return this.device?.allowPause;
		},
		allowNormalCheckTypeAssignation () {
			return this.device?.allowNormalCheckTypeAssignation;
		},
		allowCheckTypeAssignation () {
			return this.device?.allowCheckTypeAssignation;
		},
		configurations () {
			return arrayToObject(
				[
					...this.device?.configurations ?? [],
					...this.configs ?? [],
				].map(({ configuration, configurationParameters }) => {
					return {
						name: configuration,
						...configurationParameters,
					};
				}),
				'name',
			);
		},
	},
	actions: {
		// device
		resolveDeviceId (deviceId) {
			return deviceId ?? this.device?.id;
		},
		setDevice (device) {
			this.device = device;

			if (device) {
				this.setDeviceLocalConfig(device);
			}
		},
		setDeviceLocalConfig (device) {
			if (!device) return;

			console.log('📱 [useDeviceStore] setting device local config', device);

			const setters = {
				audio: setAudio,
				threshold: setThreshold,
				flash: setFlash,
				confidence: setConfidence,
				liveMode: value => setIsLiveMode(!this.isFacialRecognitionAmazon && value),
				faceDataVersion: setFaceDataVersion,

				countdown: value => this.setCountdown(value),
				employeeList: value => this.setEmployeeList(value),
				locale: value => this.setLocale(value),

				continuous: setContinuous,

				theme: value => { isDark.value = value === 'dark'; },
			};

			Object.entries(setters)
				.filter(([field]) => device[field] !== null && device[field] !== undefined)
				.forEach(([field, apply]) => apply(device[field]))
			;

			// reload faces
			if (device.lastUpdate && new Date(device.lastUpdate).getUTCFullYear() === 1970) {
				console.log('📱 [useDeviceStore] RELOADING faces', device.lastUpdate);
				setLastUpdate(null);
			}
		},

		// configurations
		setConfigs (configs) {
			this.configs = configs;
		},
		setConfig (name, parameters) {
			const configuration = this.configs.find(config => config.configuration === name);
			if (configuration) {
				configuration.configurationParameters = parameters;
				return;
			}

			const deviceConfiguration = this.device.configurations.find(config => config.configuration === name);
			if (deviceConfiguration) {
				deviceConfiguration.configurationParameters = parameters;
			}
		},
		async fetchConfigs () {
			if (!this.companies?.length) return null;

			return Promise.all([
				getWelcomeSectionConfiguration(this.companies[0]?.id),
				getCodeCredentialsConfiguration(this.companies[0]?.id),
				getFaceIdConfiguration(this.companies[0]?.id),
				getQrCodeConfiguration(this.companies[0]?.id),
				getNfcConfiguration(this.companies[0]?.id),
			]);
		},
		async getConfigs () {
			const configurations = await this.fetchConfigs();

			if (configurations) {
				this.setConfigs(configurations);
			}
		},

		// device types
		setTypes (types) {
			this.types = types;
		},
		async getTypes () {
			this.types = await getDeviceTypes(this.device?.id);
		},

		// locale
		setLocale (locale) {
			this.locale = locale;
		},

		setCountdown (countdown) {
			const seconds = Number(countdown);

			if (!Number.isFinite(seconds)) {
				return;
			}

			this.countdown = Math.min(60, Math.max(0, seconds));
		},

		setEmployeeList (employeeList) {
			this.employeeList = employeeList;
		},

		setBusy (busy) {
			this.busy = busy;
		},

		// reset
		reset () {
			this.setDevice(null);
			this.setConfigs([]);
			this.setTypes([]);
			this.setLocale(null);
		},
	},
	sockets: {
		CompanyDeviceActivated () {
			this.device.status = 'active';
		},
		CompanyDeviceDeactivated () {
			this.device.status = 'inactive';
		},
		async CompanyDeviceUpdated () {
			this.setDevice(await getDevice());
		},
		async EntityConfigurationCreated (data) {
			const {
				configuration,
				configurationParameters,
			} = parseSocketData(data);

			const name = configuration.split('\\').pop();
			this.setConfig(name, configurationParameters);
		},
		async EntityConfigurationUpdated (data) {
			const {
				configuration,
				configurationParameters,
			} = parseSocketData(data);

			const name = configuration.split('\\').pop();
			this.setConfig(name, configurationParameters);
		},
		DeviceCheckTypeCollectionAssignationUpdated () {
			this.getTypes();
		},
		CompanyDeviceDeleted () {
			this.reset();
		},
	},
});

function parseSocketData (data) {
	const result = { ...data };

	function safeJsonParse (value) {
		if (typeof value !== 'string') {
			return value;
		}

		const trimmed = value.trim();
		if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
			return value;
		}

		try {
			return JSON.parse(value);
		} catch (error) {
			console.warn('Failed to parse JSON string:', value, error);
			return value;
		}
	}

	// Parse each key in the object
	Object.keys(result).forEach(key => {
		result[key] = safeJsonParse(result[key]);
	});

	return result;
}
